import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useLeadSearchQuery } from '../../../../queries/Lead/useLeadSearchQuery';
import { Alert, AlertDescription, AlertTitle } from '../../../../web/components-v2/Alert';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { RadioGroup, RadioGroupItem } from '../../../../web/components-v2/RadioGroup';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useCoachLabCache } from './labCache';
import {
	type CallMix,
	parseRealCallInput,
	type ICoachLabLead,
	type ICoachLabVertical,
	PERSONA_WRITER_TEMPLATE_NAME,
	STEP_JUDGE_TEMPLATE_NAME,
	verticalForCoach,
} from './labLogic';
import { styles } from './styles';
import type { CoachLabCallee, ICoachLabCallSetup } from './useCoachLabCall';
import { useCoachLabPoolQuery } from '../../../../queries/CoachLab/useCoachLabPoolQuery';

type CalleeMode = 'random' | 'list' | 'search' | 'recorded' | 'real';

export type RealCallRef = { conversationId?: string; phoneCallId?: string };

const CALLEE_MODES: { mode: CalleeMode; title: string; description: string }[] = [
	{
		description:
			"One of the real leads in your vertical that a rep had a real conversation with recently, someone new each time. The person who answers is built from that lead's record and Game Plan, and is set to end the call the way real calls end: often a front desk, often a no, sometimes a meeting. You find out at the end what they were set to do.",
		mode: 'random',
		title: 'A random real lead (the usual choice)',
	},
	{
		description: 'The same real leads, so you can choose one.',
		mode: 'list',
		title: 'Pick from the list of real leads',
	},
	{
		description: 'Any lead you have in mind, by business name or company id.',
		mode: 'search',
		title: 'Search, or enter a company id',
	},
	{
		description:
			"Built from one real call, with the real person's objections and the real outcome. The hardest to argue with.",
		mode: 'recorded',
		title: 'A recorded call',
	},
	{
		description:
			'Review a call a rep actually made: the cards the rep saw, card by card. Mark the ones you would change, and ask your coach what it would have said at that moment. Paste a link from AI Coaching Review or the call log, or a conversation id.',
		mode: 'real',
		title: 'A real call a rep made',
	},
];

const COMPANY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Step = ({ number, title, children }: React.PropsWithChildren<{ number: number; title: string }>) => (
	<section className={css(styles.step)}>
		<div className={css(styles.stepHeader)}>
			<span className={css(styles.stepNumber)}>{number}</span>
			<h2 className={css(styles.stepTitle)}>{title}</h2>
		</div>
		{children}
	</section>
);

interface IPickOption {
	id: string;
	title: string;
	meta?: string;
}

/** A scrolling single-choice list, as radio rows like the lead-choice options above it. */
const PickList = ({
	options,
	value,
	onChange,
}: {
	options: IPickOption[];
	value?: string;
	onChange: (id: string) => void;
}) => (
	<RadioGroup value={value ?? ''} onValueChange={id => onChange(id as string)}>
		<div className={css(styles.pickList)}>
			{options.map(o => (
				<Label key={o.id} styles={[styles.pickItem, value === o.id && styles.pickItemSelected]}>
					<RadioGroupItem value={o.id} />
					<span className={css(styles.optionText)}>
						<span>{o.title}</span>
						{o.meta ? <span className={css(styles.pickItemMeta)}>{o.meta}</span> : null}
					</span>
				</Label>
			))}
		</div>
	</RadioGroup>
);

export const CoachLabSetup = ({
	templates,
	initialRealCall,
	onStart,
	onReview,
	opening = false,
}: {
	templates: IAIAgentTemplate[];
	/** A conversation id from a "Give coach feedback" link, which opens straight onto the real-call choice. */
	initialRealCall?: string;
	onStart: (setup: ICoachLabCallSetup, coach: IAIAgentTemplate) => void;
	onReview: (call: RealCallRef, coach: IAIAgentTemplate) => void;
	/** A call log link is being looked up. */
	opening?: boolean;
}) => {
	const { callMix, calledLeads, lastCoachId, saveCallMix, saveLastCoachId } = useCoachLabCache();
	const [mix, setMix] = React.useState<CallMix>(callMix);
	const coaching = templates.filter(t => t.conversationType === Api.AIConversationType.SalesLiveCoaching);
	const labCoaches = coaching.filter(t => t.name.startsWith('LAB '));
	const [showAll, setShowAll] = React.useState(labCoaches.length === 0);
	const last = lastCoachId();
	const coaches = (showAll ? coaching : labCoaches)
		.filter(t => !!verticalForCoach(t.name))
		.sort((a, b) => Number(b.id === last) - Number(a.id === last) || a.name.localeCompare(b.name));

	const [coachId, setCoachId] = React.useState<string | undefined>(coaches.find(c => c.id === last)?.id);
	const coach = coaches.find(c => c.id === coachId);
	const vertical: ICoachLabVertical | null = coach ? verticalForCoach(coach.name) : null;
	const pool = useCoachLabPoolQuery(vertical?.bank);
	const labProspect = vertical ? coaching.find(t => t.name === vertical.labProspectTemplateName) : undefined;
	const prospectTemplate =
		labProspect ?? (vertical ? coaching.find(t => t.name === vertical.prospectTemplateName) : undefined);
	const stepJudge = labProspect ? coaching.find(t => t.name === STEP_JUDGE_TEMPLATE_NAME) : undefined;
	const writerTemplate = coaching.find(t => t.name === PERSONA_WRITER_TEMPLATE_NAME);

	const [chosenMode, setMode] = React.useState<CalleeMode>(initialRealCall ? 'real' : 'random');
	const [realInput, setRealInput] = React.useState(initialRealCall ?? '');
	const realCall = parseRealCallInput(realInput);
	const leads = pool.data?.leads ?? [];
	const personas = pool.data?.personas ?? [];
	const availableModes = CALLEE_MODES.filter(m =>
		m.mode === 'search' || m.mode === 'real' ? true : m.mode === 'recorded' ? personas.length > 0 : leads.length > 0
	);
	// The default, a random lead, is not offered when the vertical has no lead pool, so fall back to the first choice shown.
	const mode = availableModes.some(m => m.mode === chosenMode) ? chosenMode : (availableModes[0]?.mode ?? 'search');
	const [listFilter, setListFilter] = React.useState('');
	const [pickedLead, setPickedLead] = React.useState<ICoachLabLead | null>(null);
	const [personaId, setPersonaId] = React.useState<string | null>(null);
	const [search, setSearch] = React.useState('');
	const debouncedSearch = useDebounceValue(search.trim(), 400);
	const isCompanyId = COMPANY_ID.test(debouncedSearch) && debouncedSearch === search.trim();
	const searchResults = useLeadSearchQuery({
		enabled: mode === 'search' && debouncedSearch.length >= 3 && !isCompanyId,
		search: debouncedSearch,
	});

	const filteredLeads = leads.filter(l => l.org.toLowerCase().includes(listFilter.trim().toLowerCase()));

	const pickRandom = (): ICoachLabLead | null => {
		if (leads.length === 0) return null;
		const called = new Set(calledLeads());
		const fresh = leads.filter(l => !called.has(l.companyId));
		const from = fresh.length > 0 ? fresh : leads;
		return from[Math.floor(Math.random() * from.length)];
	};

	const callee = (): CoachLabCallee | null => {
		if (mode === 'random') {
			const lead = pickRandom();
			return lead ? { companyId: lead.companyId, kind: 'lead', label: lead.org } : null;
		}
		if (mode === 'list' || mode === 'search') {
			if (mode === 'search' && isCompanyId && !pickedLead) {
				return { companyId: debouncedSearch, kind: 'lead', label: '' };
			}
			return pickedLead ? { companyId: pickedLead.companyId, kind: 'lead', label: pickedLead.org } : null;
		}
		const persona = personas.find(p => p.id === personaId);
		return persona ? { kind: 'persona', persona } : null;
	};

	const canStart =
		!!coach &&
		(mode === 'real' ? !!realCall : !!prospectTemplate) &&
		(mode === 'real'
			? true
			: mode === 'random'
				? leads.length > 0
				: mode === 'recorded'
					? !!personaId
					: !!pickedLead || (mode === 'search' && isCompanyId));

	const start = () => {
		if (mode === 'real') {
			if (coach && realCall) {
				saveLastCoachId(coach.id);
				onReview(realCall, coach);
			}
			return;
		}
		const chosen = callee();
		if (!coach || !vertical || !prospectTemplate || !chosen) return;
		saveLastCoachId(coach.id);
		onStart(
			{
				bank: personas,
				callee: chosen,
				mix,
				coachTemplateId: coach.id,
				prospectTemplateId: prospectTemplate.id,
				stepJudgeTemplateId: stepJudge?.id,
				vertical: vertical.key,
				writerTemplateId: writerTemplate?.id,
			},
			coach
		);
	};

	const changeMode = (next: CalleeMode) => {
		setMode(next);
		setPickedLead(null);
		setPersonaId(null);
	};

	return (
		<>
			<Step number={1} title='Pick your coach'>
				<p className={css(styles.help)}>
					Your sandboxes are named <strong>LAB &lt;your name&gt; (&lt;vertical&gt;)</strong>, such as{' '}
					<strong>(Legal)</strong> or <strong>(Accounting)</strong>. Each one started as an exact copy of the live
					coach, and only you and Jes change it. Reps never see them. The coach you used last is listed first.
				</p>
				{coaches.length === 0 ? <p className={css(styles.help)}>No coaching templates found.</p> : null}
				<PickList
					options={coaches.map(c => ({ id: c.id, meta: c.id === last ? 'Last used' : undefined, title: c.name }))}
					value={coachId}
					onChange={id => {
						setCoachId(id);
						setPickedLead(null);
						setPersonaId(null);
					}}
				/>
				<Label>
					<Checkbox checked={showAll} onCheckedChange={checked => setShowAll(!!checked)} />
					<span>Show every coaching template, not just the LAB sandboxes</span>
				</Label>
				{coach && !prospectTemplate ? (
					<Alert tone='warning'>
						<AlertTitle>No AI prospect for this vertical</AlertTitle>
						<AlertDescription>
							Coach Lab needs a template named "{vertical?.prospectTemplateName}" to play the prospect.
						</AlertDescription>
					</Alert>
				) : null}
			</Step>

			<Step number={2} title='Who are you calling?'>
				<p className={css(styles.help)}>
					The same lead is the same person every time you call them, so you can retry a call after your coach is
					updated.
				</p>
				{!coach ? (
					<p className={css(styles.help)}>Pick a coach first.</p>
				) : pool.isPending ? (
					<LoadingSpinner />
				) : (
					<>
						{pool.isError ? (
							<Alert tone='warning'>
								<AlertTitle>Could not load the real leads for this vertical</AlertTitle>
								<AlertDescription>You can still search for a lead, or enter a company id.</AlertDescription>
							</Alert>
						) : null}
						<RadioGroup value={mode} onValueChange={value => changeMode(value as CalleeMode)}>
							<div className={css(styles.optionList)}>
								{availableModes.map(m => (
									<Label key={m.mode} styles={[styles.option, mode === m.mode && styles.optionSelected]}>
										<RadioGroupItem value={m.mode} />
										<span className={css(styles.optionText)}>
											<span className={css(styles.optionTitle)}>{m.title}</span>
											<span className={css(styles.help)}>{m.description}</span>
										</span>
									</Label>
								))}
							</div>
						</RadioGroup>

						{mode === 'list' ? (
							<>
								<Input
									placeholder='Type to filter the list'
									value={listFilter}
									onChange={e => setListFilter(e.target.value)}
								/>
								<PickList
									options={filteredLeads.map(l => ({ id: l.companyId, title: l.org }))}
									value={pickedLead?.companyId}
									onChange={id => setPickedLead(leads.find(l => l.companyId === id) ?? null)}
								/>
							</>
						) : null}

						{mode === 'search' ? (
							<>
								<Input
									placeholder='Business name, or a company id'
									value={search}
									onChange={e => {
										setSearch(e.target.value);
										setPickedLead(null);
									}}
								/>
								{isCompanyId ? (
									<p className={css(styles.help)}>That looks like a company id. Start the call to use it.</p>
								) : searchResults.isFetching ? (
									<LoadingSpinner />
								) : (
									<PickList
										options={(searchResults.data ?? [])
											.filter(r => !!r.company?.id)
											.map(r => ({ id: r.company!.id!, title: r.company!.companyName ?? r.company!.id! }))}
										value={pickedLead?.companyId}
										onChange={id => {
											const company = searchResults.data?.find(r => r.company?.id === id)?.company;
											setPickedLead({ companyId: id, org: company?.companyName ?? '' });
										}}
									/>
								)}
							</>
						) : null}

						{mode === 'random' || mode === 'list' || mode === 'search' ? (
							<Label>
								<Checkbox
									checked={mix === 'longer'}
									onCheckedChange={checked => {
										const next: CallMix = checked ? 'longer' : 'real';
										setMix(next);
										saveCallMix(next);
									}}
								/>
								<span className={css(styles.optionText)}>
									<span className={css(styles.optionTitle)}>Longer calls</span>
									<span className={css(styles.help)}>
										Skip prospects set to stop you at the front desk or the hook, so every call reaches the pitch and
										the objections. Leave it off to call the real mix, where almost half of calls end in the first few
										lines.
									</span>
								</span>
							</Label>
						) : null}

						{mode === 'real' ? (
							<>
								<Input
									placeholder='Link from AI Coaching Review or the call log, or a conversation id'
									value={realInput}
									onChange={e => setRealInput(e.target.value)}
								/>
								{realInput.trim() && !realCall ? (
									<p className={css(styles.help)}>That isn't a review link, a call log link or a conversation id.</p>
								) : null}
							</>
						) : null}

						{mode === 'recorded' ? (
							<PickList
								options={personas.map(p => ({ id: p.id, meta: `${p.contactName}, ${p.role}`, title: p.org }))}
								value={personaId ?? undefined}
								onChange={setPersonaId}
							/>
						) : null}
					</>
				)}
			</Step>

			<Step number={3} title={mode === 'real' ? 'Open the call' : 'Start the call'}>
				<p className={css(styles.help)}>
					{mode === 'real'
						? 'The call opens card by card. Your coach is only asked when you press "What would my coach say here?" on a card.'
						: 'The real Game Plan for the lead appears first. The first call on a lead can take a few minutes while it is written. After that it is instant. Then the prospect picks up the phone.'}
				</p>
				<div className={css(styles.actions)}>
					<Button variant='primary' disabled={!canStart || opening} isLoading={opening} onClick={start}>
						{mode === 'real' ? 'Open the call' : 'Start the call'}
					</Button>
				</div>
			</Step>
		</>
	);
};
